<template>
  <AppEventContainer feature="Home">
    <AppScreenTitle title="Search Upcoming Events">
    </AppScreenTitle>
    <EventSearch @search-text-change="handleSearchTextChange" @change-filter="showFilter = true" />
    <AppModal :show-save="false" :show-cancel="false" v-model="showFilter">
      <EventFilter class="mb-4" :default-filters="currentFilters" @filters-applied="filtersApplied" />
    </AppModal>
    <!-- Loading Spinner -->
    <AppLoading v-if="isLoading" :loading="isLoading"></AppLoading>

    <!-- Events Grid -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <template v-for="pokerRun in pokerRuns" :key="`event-${pokerRun.id}`">
        <div class="w-full">
          <!-- Use EventListItemHand for registered active events -->
          <EventListItemHand v-if="isRegisteredEvent(pokerRun) && isEventActive(pokerRun)"
            class="h-full cursor-pointer transition-all duration-200 hover:shadow-lg" :event="pokerRun"
            @click="openItem(pokerRun)" />
          <!-- Use EventListItem for non-registered events and registered non-active events -->
          <div v-else class="relative h-full">
            <!-- Registered indicator for non-active registered events -->

            <EventListItem class="h-full cursor-pointer transition-all duration-200 hover:shadow-lg"
              :isRegistered="isRegisteredEvent(pokerRun) && !isEventActive(pokerRun)":poker-run-event="pokerRun" :show-type="true" @click="openItem(pokerRun)">
              
            </EventListItem>
          </div>
        </div>
      </template>
    </div>
  </AppEventContainer>
</template>
<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import EventListItem from "@/components/EventListItem.vue";
import EventListItemHand from "@/components/EventListItemHand.vue";
import PokerRunEventDataService from "@/services/PokerRunEventDataService";
import { PokerRunEvent } from "@models/PokerRunEvent";
import { GeoPoint } from "firebase/firestore";
import EventSearch from "@/components/EventSearch.vue";
import EventFilter from "@/components/EventFilter.vue";
import useEvent from "@/modules/useEvent";
import { EventFilters } from "@/models/EventFilters";
import { useUserProfileStore } from "@/store/UserProfile";
import AppLoading from "@/components/AppLoading.vue";
import SystemDataService from "@/services/SystemDataService";

const router = useRouter();
const { isFreeEvent, isEventActive } = useEvent();
const userProfileStore = useUserProfileStore();
const pokerRuns = ref<PokerRunEvent[]>([]);
let pokerRunData: PokerRunEvent[] = [];
const searhText = ref("");
const coor = ref(new GeoPoint(0, 0));
const showFilter = ref(false);
const userRegisteredEvents = ref<PokerRunEvent[]>([]);
const isLoading = ref(true);

// Default filters
const defaultFilters: EventFilters = {
  dateFrom: null,
  dateTo: null,
  excludeHandFee: false,
  address: {
    addressLine1: '',
    city: '',
    state: '',
    postalCode: '',
    name: '',
    location: null
  },
  coords: {
    latitude: coor.value.latitude,
    longitude: coor.value.longitude
  },
  distance: 25, // Default to 25 miles
  eventType: null
};

const currentFilters = ref<EventFilters>(defaultFilters);

const demoEvent = ref<PokerRunEvent | null>(null);

const loadDemoEvent = async () => {
  const systemData = await SystemDataService.getActive();
  if (
    systemData?.demoEventId
  ) {
    demoEvent.value = await PokerRunEventDataService.get(systemData.demoEventId);
  }
};

// Function removed as it's not being used

// Check if an event is in the user's registered events
const isRegisteredEvent = (event: PokerRunEvent): boolean => {
  return userRegisteredEvents.value.some(registeredEvent => registeredEvent.id === event.id);
};

// Open event - route to RegisteredEvent for registered events, AcesEvent for non-registered
const openItem = (pokerRun: PokerRunEvent) => {
  if (isRegisteredEvent(pokerRun)) {
    router.push({ name: "RegisteredEvent", params: { id: pokerRun.id } });
  } else {
    router.push({ name: "AcesEvent", params: { id: pokerRun.id } });
  }
};

const search = async () => {
  isLoading.value = true;
  try {
    // First get events based on location and distance
    const items = await PokerRunEventDataService.search(coor.value, currentFilters.value.distance);

    // Filter out excluded and private events
    pokerRunData = items.filter(x =>
      x.eventSettings.excludeInSearchResults === undefined ||
      !x.eventSettings.excludeInSearchResults
    );

    // Apply text search to filtered results
    applyTextSearch();
  } catch (error) {
    console.error("Error searching events:", error);
    pokerRunData = [];
    pokerRuns.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Separate function to handle text search filtering
const applyTextSearch = () => {
  if (!searhText.value) {
    pokerRuns.value = [...pokerRunData];
    return;
  }

  const searchLower = searhText.value.toLowerCase();
  pokerRuns.value = pokerRunData.filter(event =>
    event.name.toLowerCase().includes(searchLower)
  );
};

// Update the search text handling
const handleSearchTextChange = (newText: string) => {
  searhText.value = newText.toLowerCase();
  applyTextSearch(); // Only apply text search without triggering main search
};

const setFilter = (coords: { latitude: number; longitude: number }) => {
  coor.value = new GeoPoint(coords.latitude, coords.longitude);
  showFilter.value = false;
  search();
};

const geolocationCallback = (location: {
  coords: { latitude: number; longitude: number };
}) => {
  const coords = {
    latitude: location.coords.latitude,
    longitude: location.coords.longitude
  };

  currentFilters.value = {
    ...currentFilters.value,
    coords,
    address: {
      addressLine1: '',
      city: '',
      state: '',
      postalCode: '',
      name: 'Current Location',
      location: {
        _lat: coords.latitude,
        _long: coords.longitude
      }
    }
  };

  setFilter(coords);
};

const errorHandler = (error: { code: number }) => {
  if (error.code === 1) {
    console.log("Error: PERMISSION_DENIED: User denied access to their location");
  } else if (error.code === 2) {
    console.log("Error: POSITION_UNAVAILABLE: Network is down.");
  } else if (error.code === 3) {
    console.log("Error: TIMEOUT: Calculating the user's location too took long");
  } else {
    console.log("Unexpected error code");
  }
};

const getLocation = () => {
  if (typeof navigator !== "undefined" && typeof navigator.geolocation !== "undefined") {
    console.log("Asking user to get their location");
    navigator.geolocation.getCurrentPosition(geolocationCallback, errorHandler);
  } else {
    console.log("Your browser does not support the HTML5 Geolocation API.");
  }
};

const filtersApplied = async (filters: EventFilters) => {
  console.log("Filters applied:", filters);

  // Update coordinates if provided
  if (filters.coords) {
    console.log("Updating coordinates:", filters.coords);
    coor.value = new GeoPoint(filters.coords.latitude, filters.coords.longitude);
    await search(); // Trigger new search with updated coordinates
    console.log(pokerRunData);
  }

  pokerRuns.value = pokerRunData.filter(event => {
    // Filter by date range
    if (filters.dateFrom || filters.dateTo) {
      // Handle event date safely by ensuring it's a string
      const eventDateStr = typeof event.eventDate === 'string' ? event.eventDate : event.eventDate.toDate().toISOString();
      const eventDate = new Date(eventDateStr);

      if (filters.dateFrom && eventDate < filters.dateFrom) {
        return false;
      }

      if (filters.dateTo && eventDate > filters.dateTo) {
        return false;
      }
    }

    // Filter by hand fee
    if (filters.excludeHandFee && !isFreeEvent(event)) {
      return false;
    }

    // Filter by event type
    if (filters.eventType && event.eventType !== filters.eventType) {
      return false;
    }

    // Include events that pass all filters
    return true;
  });

  currentFilters.value = filters;
  showFilter.value = false;
};

// Load user's registered events
const loadUserRegisteredEvents = async () => {
  if (userProfileStore.profile.id) {
    try {
      userRegisteredEvents.value = await PokerRunEventDataService.getUserPokerRuns(userProfileStore.profile.id);
    } catch (error) {
      console.error("Error loading user's registered events:", error);
      userRegisteredEvents.value = [];
    }
  }
};

const initalize = async () => {
  isLoading.value = true;
  try {
    getLocation(); // This triggers the search which sets isLoading to false when complete
    await loadUserRegisteredEvents();
    await loadDemoEvent();
    if (demoEvent.value) {
      setTimeout(() => {
        pokerRuns.value = [demoEvent.value, ...pokerRuns.value.filter(x => x.id !== demoEvent.value.id)];
      }, 1000);
    }

  } catch (error) {
    console.error("Error initializing:", error);
    isLoading.value = false;
  }
};

onMounted(() => {
  initalize();
});
</script>
